/**
 * Hook para usar Brand Emojis da Auli de forma semântica
 * 
 * Mapeia contextos (success, error, empty, etc.) para assets do banco
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getBrandAssetUrl, type BrandAsset } from "./useBrandAssets";

// Mapeamento semântico de contextos para asset_ids
// Baseado nos emojis disponíveis no banco de dados
export const EMOJI_CONTEXT_MAP = {
  // Feedbacks positivos
  success: ["05-elementos-gra-ficos-legal-amarelo", "05-elementos-gra-ficos-legal-verde"],
  celebration: ["05-elementos-gra-ficos-beijinho-amarelo", "05-elementos-gra-ficos-beijinho-verde"],
  approval: ["05-elementos-gra-ficos-joia-amarelo", "05-elementos-gra-ficos-joia-verde"],
  
  // Feedbacks negativos/neutros
  error: ["05-elementos-gra-ficos-confuso-amarelo", "05-elementos-gra-ficos-confuso-rosa"],
  warning: ["05-elementos-gra-ficos-surpreso-amarelo", "05-elementos-gra-ficos-surpreso-rosa"],
  
  // Empty states
  empty: ["05-elementos-gra-ficos-duvida-amarelo", "05-elementos-gra-ficos-duvida-verde"],
  notFound: ["05-elementos-gra-ficos-confuso-verde", "05-elementos-gra-ficos-confuso-amarelo"],
  
  // Interações
  pointing: ["05-elementos-gra-ficos-apontando-amarelo", "05-elementos-gra-ficos-apontando-verde"],
  idea: ["05-elementos-gra-ficos-sorriso-verde", "05-elementos-gra-ficos-sorriso-amarelo"],
  
  // Estados especiais
  loading: ["05-elementos-gra-ficos-oculos"],
  info: ["05-elementos-gra-ficos-oculos"],
} as const;

export type EmojiContext = keyof typeof EMOJI_CONTEXT_MAP;

// Fallbacks Unicode para quando assets não carregarem
const UNICODE_FALLBACKS: Record<EmojiContext, string> = {
  success: "✅",
  celebration: "🎉",
  approval: "👍",
  error: "❌",
  warning: "⚠️",
  empty: "🤔",
  notFound: "🔍",
  pointing: "👆",
  idea: "💡",
  loading: "⏳",
  info: "ℹ️",
};

interface BrandEmojiResult {
  asset: BrandAsset | null;
  url: string | null;
  fallbackEmoji: string;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook que retorna um Brand Emoji baseado no contexto semântico
 * 
 * @param context - Contexto semântico (success, error, empty, etc.)
 * @param options - Opções de transformação de imagem
 * @returns Asset, URL e fallback Unicode
 * 
 * @example
 * const { url, fallbackEmoji, isLoading } = useBrandEmoji("success");
 * // Renderiza imagem do asset ou fallback Unicode
 */
export const useBrandEmoji = (
  context: EmojiContext,
  options?: { width?: number; height?: number }
): BrandEmojiResult => {
  const assetIds = EMOJI_CONTEXT_MAP[context];
  const primaryAssetId = assetIds[0];

  const { data: asset, isLoading, error } = useQuery({
    queryKey: ["brand-emoji", primaryAssetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_assets")
        .select("*")
        .eq("asset_id", primaryAssetId)
        .eq("is_active", true)
        .single();

      if (error) throw error;
      return data as BrandAsset;
    },
    staleTime: 1000 * 60 * 60, // Cache por 1 hora
    gcTime: 1000 * 60 * 60 * 24, // Mantém em cache por 24h
  });

  const url = asset 
    ? getBrandAssetUrl(asset.storage_path, options)
    : null;

  return {
    asset: asset || null,
    url,
    fallbackEmoji: UNICODE_FALLBACKS[context],
    isLoading,
    error: error as Error | null,
  };
};

/**
 * Hook que retorna múltiplos Brand Emojis de uma vez
 * Útil para listas de conquistas, badges, etc.
 */
export const useBrandEmojis = (assetIds: string[]) => {
  return useQuery({
    queryKey: ["brand-emojis", assetIds],
    queryFn: async () => {
      if (assetIds.length === 0) return [];

      const { data, error } = await supabase
        .from("brand_assets")
        .select("*")
        .in("asset_id", assetIds)
        .eq("is_active", true);

      if (error) throw error;
      return data as BrandAsset[];
    },
    enabled: assetIds.length > 0,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });
};

/**
 * Hook que retorna todos os emojis disponíveis (categoria "emojis")
 */
export const useAllBrandEmojis = () => {
  return useQuery({
    queryKey: ["brand-emojis-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_assets")
        .select("*")
        .eq("category", "emojis")
        .eq("is_active", true)
        .order("display_name");

      if (error) throw error;
      return data as BrandAsset[];
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });
};
